/**
 * 前端逻辑流代码生成器 - 离线预览用
 * 将 LogicFlowGraph 转换为 Freqtrade Python 策略代码
 */

import type {
    ConditionRule,
    IndicatorMode,
    IndicatorNodeConfig,
    LogicFlowEdge,
    LogicFlowGraph,
    LogicFlowNode,
    RuleNodeConfig,
    StrategyConfig,
} from '../types/logicFlow';

// ========== 拓扑排序 ==========
function topologicalSort(graph: LogicFlowGraph): LogicFlowNode[] {
    const inDegree = new Map<string, number>();
    const adj = new Map<string, string[]>();

    for (const node of graph.nodes) {
        inDegree.set(node.id, 0);
        adj.set(node.id, []);
    }
    for (const edge of graph.edges) {
        inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
        const neighbors = adj.get(edge.source);
        if (neighbors) {
            neighbors.push(edge.target);
        }
    }

    const queue: string[] = [];
    for (const [nodeId, degree] of inDegree) {
        if (0 === degree) {
            queue.push(nodeId);
        }
    }

    const sorted: LogicFlowNode[] = [];
    const nodeMap = new Map<string, LogicFlowNode>();
    for (const node of graph.nodes) {
        nodeMap.set(node.id, node);
    }

    while (queue.length > 0) {
        const nodeId = queue.shift()!;
        const node = nodeMap.get(nodeId);
        if (node) {
            sorted.push(node);
        }
        for (const neighbor of adj.get(nodeId) || []) {
            const newDegree = (inDegree.get(neighbor) || 1) - 1;
            inDegree.set(neighbor, newDegree);
            if (0 === newDegree) {
                queue.push(neighbor);
            }
        }
    }

    return sorted;
}

/**
 * 检测 Dict 节点 rawExpression 中的 dataframe['xxx'] 引用，返回隐式 column→Dict 边
 */
function detectImplicitColumnToDictEdges(graph: LogicFlowGraph): LogicFlowEdge[] {
    const implicitEdges: LogicFlowEdge[] = [];

    // 构建 column indicatorName → nodeId 映射
    const colNameToNodeId = new Map<string, string>();
    for (const node of graph.nodes) {
        if ('INDICATOR' !== node.type) continue;
        const config = node.config as IndicatorNodeConfig;
        if ('Dict' !== (config.outputType || 'column')) {
            colNameToNodeId.set(config.indicatorName, node.id);
        }
    }

    if (0 === colNameToNodeId.size) return implicitEdges;

    // 扫描 Dict 节点的 rawExpression
    const refRegex = /dataframe\['([^']+)'\]|dataframe\["([^"]+)"\]/g;
    let edgeId = graph.edges.length;

    for (const node of graph.nodes) {
        if ('INDICATOR' !== node.type) continue;
        const config = node.config as IndicatorNodeConfig;
        if ('Dict' !== (config.outputType || 'column')) continue;

        const rawExpr = config.rawExpression || '';
        if (!rawExpr) continue;

        refRegex.lastIndex = 0;
        let match: RegExpExecArray | null;
        while (null !== (match = refRegex.exec(rawExpr))) {
            const refName = match[1] || match[2];
            const colNodeId = colNameToNodeId.get(refName);
            if (colNodeId) {
                const exists = graph.edges.some(e => e.source === colNodeId && e.target === node.id);
                if (!exists) {
                    implicitEdges.push({
                        id: `_implicit_col_dict_${edgeId++}`,
                        source: colNodeId,
                        target: node.id,
                    });
                }
            }
        }
    }

    return implicitEdges;
}

// ========== 反向邻接表（target → sources） ==========
function buildReverseAdj(graph: LogicFlowGraph): Map<string, string[]> {
    const revAdj = new Map<string, string[]>();
    for (const node of graph.nodes) {
        revAdj.set(node.id, []);
    }
    for (const edge of graph.edges) {
        const neighbors = revAdj.get(edge.target);
        if (neighbors) {
            neighbors.push(edge.source);
        }
    }
    return revAdj;
}

// ========== 收集上游无动作 RULE 节点的条件 ==========
function collectUpstreamNoActionConditions(
    nodeId: string,
    revAdj: Map<string, string[]>,
    nodeMap: Map<string, LogicFlowNode>,
    visited: Set<string>
): string | null {
    if (visited.has(nodeId)) return null;
    visited.add(nodeId);

    const upstreamIds = revAdj.get(nodeId) || [];
    const conditions: string[] = [];

    for (const upstreamId of upstreamIds) {
        const upstreamNode = nodeMap.get(upstreamId);
        if (!upstreamNode || 'RULE' !== upstreamNode.type) continue;

        const rc = upstreamNode.config as RuleNodeConfig;
        if ('none' !== rc.action) continue;

        // 递归收集更深层的条件
        const deeperCond = collectUpstreamNoActionConditions(upstreamId, revAdj, nodeMap, visited);
        const ownCond = 0 === rc.rules.length ? null : buildConditionExpression(rc.rules, rc.logicOperator);

        // 将该节点自身条件 + 更深层条件，按该节点的 logicOperator 合并
        let combined: string | null = null;
        if (deeperCond && ownCond) {
            const joiner = 'AND' === rc.logicOperator ? ' &\n            ' : ' |\n            ';
            combined = `(${deeperCond}) ${joiner} (${ownCond})`;
        } else {
            combined = deeperCond || ownCond;
        }
        if (combined) {
            conditions.push(`(${combined})`);
        }
    }

    if (0 === conditions.length) return null;
    // 多个上游分支之间用 AND 连接（都是前置条件）
    return conditions.join(' &\n            ');
}

function buildRuleExpression(rule: ConditionRule): string {
    const {leftOperand, operator, rightOperand} = rule;
    switch (operator) {
        case 'cross_above':
            return `(qtpylib.crossed_above(${leftOperand}, ${rightOperand}))`;
        case 'cross_below':
            return `(qtpylib.crossed_below(${leftOperand}, ${rightOperand}))`;
        default:
            return `(${leftOperand} ${operator} ${rightOperand})`;
    }
}

function buildConditionExpression(rules: ConditionRule[], logicOp: string): string {
    const ruleExpressions = rules.map(buildRuleExpression);
    const joiner = 'AND' === logicOp ? ' &\n            ' : ' |\n            ';
    return ruleExpressions.join(joiner);
}

// ========== 指标代码生成 ==========
function resolveIndicatorMode(config: IndicatorNodeConfig): IndicatorMode {
    // CUSTOM 函数永远视为自定义模式，无论 config.mode 取值
    if ('CUSTOM' === config.function) {
        return 'custom';
    }
    if (config.mode) {
        // raw 模式下有有效标准函数 + 参数时，按标准模式处理（参数可实时编辑）
        if ('raw' === config.mode && config.function
            && Array.isArray(config.params) && config.params.length > 0) {
            return 'standard';
        }
        return config.mode;
    }
    if (config.customExpression) {
        return 'custom';
    }
    if (config.rawExpression) {
        return 'raw';
    }
    return 'standard';
}

export function buildIndicatorFunctionCall(config: IndicatorNodeConfig): string {
    const params = config.params || [];
    const columnRef = params.find(p => 'column' === p.type);
    const otherParams = params.filter(p => 'column' !== p.type);

    const parts: string[] = [];
    if (columnRef) {
        parts.push(`dataframe['${columnRef.value}']`);
    }

    for (const param of otherParams) {
        if ('number' === param.type) {
            parts.push(`${param.name}=${param.value}`);
        } else if ('expression' === param.type) {
            if (param.name.startsWith('param_')) {
                parts.push(param.value);
            } else {
                parts.push(`${param.name}=${param.value}`);
            }
        } else {
            parts.push(`${param.name}='${param.value}'`);
        }
    }

    if (0 === parts.length) {
        parts.push(`dataframe['close']`);
    }

    const outputAccess = config.outputKey ? `['${config.outputKey}']` : '';
    return `${config.function}(${parts.join(', ')})${outputAccess}`;
}

function generateIndicatorLine(node: LogicFlowNode, upstreamVarName?: string): string {
    const config = node.config as IndicatorNodeConfig;
    const name = config.indicatorName;
    const mode = resolveIndicatorMode(config);
    const outputType = config.outputType || 'column';

    // 根据输出类型决定左侧
    const lhs = 'Dict' === outputType ? `${name}` : `dataframe['${name}']`;

    if ('custom' === mode) {
        return `${lhs} = ${config.customExpression || config.rawExpression || ''}`;
    }
    if ('raw' === mode && config.rawExpression) {
        return `${lhs} = ${config.rawExpression}`;
    }

    // 有上游 Dict 引用时，使用变量引用而非完整函数调用
    if (upstreamVarName && config.outputKey) {
        return `${lhs} = ${upstreamVarName}['${config.outputKey}']`;
    }

    return `${lhs} = ${buildIndicatorFunctionCall(config)}`;
}

function getRawDependencies(node: LogicFlowNode): string[] {
    const config = node.config as IndicatorNodeConfig;
    return Array.isArray(config.rawDependencies) ? config.rawDependencies.filter(Boolean) : [];
}

function generateIndicatorDependencyLines(node: LogicFlowNode, emitted?: Set<string>): string[] {
    const config = node.config as IndicatorNodeConfig;
    if ('standard' === resolveIndicatorMode(config)) {
        return [];
    }

    const lines: string[] = [];
    for (const dependency of getRawDependencies(node)) {
        if (emitted && emitted.has(dependency)) {
            continue;
        }
        emitted?.add(dependency);
        lines.push(dependency);
    }
    return lines;
}

// ========== 单节点代码生成 ==========
export function generateNodeCode(node: LogicFlowNode, graph: LogicFlowGraph): string {
    if ('INDICATOR' === node.type) {
        const config = node.config as IndicatorNodeConfig;
        // 检查是否有上游 INDICATOR 边（多级指标）
        const revIndAdj = buildReverseAdj(graph);
        const upstreamIds = revIndAdj.get(node.id) || [];
        const nodeMap = new Map(graph.nodes.map(n => [n.id, n]));
        const hasUpstreamIndicator = upstreamIds.some(uid => {
            const un = nodeMap.get(uid);
            return un && 'INDICATOR' === un.type;
        });
        // 有上游 INDICATOR 边时跳过 rawDependencies（已被边替代）
        const depLines = hasUpstreamIndicator ? [] : generateIndicatorDependencyLines(node);
        // 获取上游 Dict 节点变量名
        let upstreamVarName: string | undefined;
        if (hasUpstreamIndicator) {
            for (const uid of upstreamIds) {
                const un = nodeMap.get(uid);
                if (un && 'INDICATOR' === un.type) {
                    const uc = un.config as IndicatorNodeConfig;
                    if ('Dict' === (uc.outputType || 'column')) {
                        upstreamVarName = uc.indicatorName;
                    }
                    break;
                }
            }
        }
        return [...depLines, generateIndicatorLine(node, upstreamVarName)].join('\n');
    }
    if ('RULE' === node.type) {
        const rc = node.config as RuleNodeConfig;

        // 自身条件
        const ownCond = rc.rules.length > 0
            ? buildConditionExpression(rc.rules, rc.logicOperator) : null;

        // 上游无动作条件
        const nodeMap = new Map(graph.nodes.map(n => [n.id, n]));
        const revAdj = buildReverseAdj(graph);
        const upstreamCond = collectUpstreamNoActionConditions(node.id, revAdj, nodeMap, new Set());

        // 交易信号行：已内嵌条件，直接输出最终语句
        if ('none' !== rc.action) {
            let finalCond = ownCond;
            if (upstreamCond && finalCond) {
                const joiner = 'AND' === rc.logicOperator ? ' &\n            ' : ' |\n            ';
                finalCond = `(${upstreamCond}) ${joiner} (${finalCond})`;
            } else if (upstreamCond) {
                finalCond = upstreamCond;
            }
            if (finalCond) {
                return `dataframe.loc[\n    ${finalCond},\n    '${rc.action}'\n] = ${rc.signalValue || '1'}`;
            }
            return `dataframe.loc[:, '${rc.action}'] = ${rc.signalValue || '1'}`;
        }

        // 无动作节点仅展示条件
        const lines: string[] = [];
        if (ownCond) lines.push(ownCond);
        if (upstreamCond) lines.push(`# 上游条件:\n(${upstreamCond})`);
        return lines.join('\n');
    }
    return '# 未知节点类型';
}

// ========== 主入口：生成 Python 策略代码 ==========
export function generateStrategyCode(graph: LogicFlowGraph, config: StrategyConfig, className: string): string {
    // 补全隐式 column→Dict 边（Dict 的 rawExpression 引用 dataframe['xxx']）
    const augmentedEdges = [...graph.edges, ...detectImplicitColumnToDictEdges(graph)];
    const augmentedGraph: LogicFlowGraph = {...graph, edges: augmentedEdges};

    const sorted = topologicalSort(augmentedGraph);

    // 1. 分桶：RULE 节点自带 action，直接按 action 类型分桶（排除无动作节点）
    const indicatorNodes = sorted.filter(n => 'INDICATOR' === n.type);
    const ruleNodes = sorted.filter(n => 'RULE' === n.type);

    const nodeMap = new Map<string, LogicFlowNode>();
    for (const node of augmentedGraph.nodes) {
        nodeMap.set(node.id, node);
    }
    const revAdj = buildReverseAdj(augmentedGraph);

    const entryRules = ruleNodes.filter(n => {
        const rc = n.config as RuleNodeConfig;
        return 'enter_long' === rc.action || 'enter_short' === rc.action;
    });
    const exitRules = ruleNodes.filter(n => {
        const rc = n.config as RuleNodeConfig;
        return 'exit_long' === rc.action || 'exit_short' === rc.action;
    });

    // 2. 收集 import（默认包含所有常用依赖）
    const imports = new Set<string>();
    imports.add('from freqtrade.strategy import IStrategy');
    imports.add('from typing import Dict, List');
    imports.add('from functools import reduce');
    imports.add('from pandas import DataFrame');
    imports.add('import talib.abstract as ta');
    imports.add('import freqtrade.vendor.qtpylib.indicators as qtpylib');
    imports.add('import numpy');

    // 3. 生成 indicators 方法体
    let indicatorsBody = '';
    if (indicatorNodes.length > 0) {
        // 构建反向邻接表（检测 INDICATOR → INDICATOR 边）
        const revIndAdj = buildReverseAdj(augmentedGraph);
        // 构建出边集合（检测孤立的 Dict 节点）
        const outEdges = new Map<string, string[]>();
        for (const n of augmentedGraph.nodes) outEdges.set(n.id, []);
        for (const e of augmentedGraph.edges) {
            const neighbors = outEdges.get(e.source);
            if (neighbors) neighbors.push(e.target);
        }

        const emittedRawDependencies = new Set<string>();
        for (const node of indicatorNodes) {
            const config = node.config as IndicatorNodeConfig;
            const outputType = config.outputType || 'column';

            // 孤立的 Dict 节点跳过（无出边）
            if ('Dict' === outputType) {
                const outgoing = outEdges.get(node.id) || [];
                if (0 === outgoing.length) continue;
            }

            // 有上游 INDICATOR 边的 column 节点：跳过 rawDependencies（已被边替代）
            const upstreamIds = revIndAdj.get(node.id) || [];
            const hasUpstreamIndicator = upstreamIds.some(uid => {
                const un = nodeMap.get(uid);
                return un && 'INDICATOR' === un.type;
            });
            if (!hasUpstreamIndicator) {
                for (const dependency of generateIndicatorDependencyLines(node, emittedRawDependencies)) {
                    indicatorsBody += `        ${dependency}\n`;
                }
            }
            // 获取上游 Dict 节点变量名
            let upstreamVarName: string | undefined;
            if (hasUpstreamIndicator) {
                for (const uid of upstreamIds) {
                    const un = nodeMap.get(uid);
                    if (un && 'INDICATOR' === un.type) {
                        const uc = un.config as IndicatorNodeConfig;
                        if ('Dict' === (uc.outputType || 'column')) {
                            upstreamVarName = uc.indicatorName;
                        }
                        break;
                    }
                }
            }
            indicatorsBody += `        ${generateIndicatorLine(node, upstreamVarName)}\n`;
        }
        indicatorsBody += '        return dataframe';
    } else {
        indicatorsBody = '        return dataframe';
    }

    // 4. 生成 entry 方法体（RULE 节点自带条件和动作）
    let entryBody = "        dataframe.loc[:, 'enter_long'] = 0\n";
    if (config.canShort) {
        entryBody += "        dataframe.loc[:, 'enter_short'] = 0\n";
    }
    entryBody += '\n';

    for (const ruleNode of entryRules) {
        const rc = ruleNode.config as RuleNodeConfig;
        const ownCond = 0 === rc.rules.length ? null : buildConditionExpression(rc.rules, rc.logicOperator);
        const upstreamCond = collectUpstreamNoActionConditions(ruleNode.id, revAdj, nodeMap, new Set());

        // 合并上游条件与自身条件
        let finalCond: string;
        if (upstreamCond && ownCond) {
            const joiner = 'AND' === rc.logicOperator ? ' &\n            ' : ' |\n            ';
            finalCond = `(${upstreamCond}) ${joiner} (${ownCond})`;
        } else {
            finalCond = upstreamCond || ownCond || '';
        }
        if (!finalCond) continue;
        entryBody += `        dataframe.loc[\n            ${finalCond},\n            '${rc.action}'\n        ] = ${rc.signalValue || '1'}\n`;
    }
    entryBody += '        return dataframe';

    // 5. 生成 exit 方法体
    let exitBody = "        dataframe.loc[:, 'exit_long'] = 0\n";
    if (config.canShort) {
        exitBody += "        dataframe.loc[:, 'exit_short'] = 0\n";
    }
    exitBody += '\n';

    for (const ruleNode of exitRules) {
        const rc = ruleNode.config as RuleNodeConfig;
        const ownCond = 0 === rc.rules.length ? null : buildConditionExpression(rc.rules, rc.logicOperator);
        const upstreamCond = collectUpstreamNoActionConditions(ruleNode.id, revAdj, nodeMap, new Set());

        let finalCond: string;
        if (upstreamCond && ownCond) {
            const joiner = 'AND' === rc.logicOperator ? ' &\n            ' : ' |\n            ';
            finalCond = `(${upstreamCond}) ${joiner} (${ownCond})`;
        } else {
            finalCond = upstreamCond || ownCond || '';
        }
        if (!finalCond) continue;
        exitBody += `        dataframe.loc[\n            ${finalCond},\n            '${rc.action}'\n        ] = ${rc.signalValue || '1'}\n`;
    }
    exitBody += '        return dataframe';

    // 6. 拼接完整代码
    const lines: string[] = [];
    for (const imp of Array.from(imports).sort()) {
        lines.push(imp);
    }
    lines.push('');
    lines.push(`class ${className}(IStrategy):`);
    lines.push(`    timeframe = '${config.timeframe}'`);
    lines.push(`    can_short = ${config.canShort ? 'True' : 'False'}`);
    lines.push(`    stoploss = ${config.stoploss}`);

    const roiEntries = Object.entries(config.minimalRoi)
        .map(([k, v]) => `"${k}": ${v}`)
        .join(', ');
    lines.push(`    minimal_roi = {${roiEntries}}`);
    lines.push('');
    lines.push('    def populate_indicators(self, dataframe: DataFrame, metadata: dict) -> DataFrame:');
    lines.push(indicatorsBody);
    lines.push('');
    lines.push('    def populate_entry_trend(self, dataframe: DataFrame, metadata: dict) -> DataFrame:');
    lines.push(entryBody);
    lines.push('');
    lines.push('    def populate_exit_trend(self, dataframe: DataFrame, metadata: dict) -> DataFrame:');
    lines.push(exitBody);
    lines.push('');

    return lines.join('\n');
}
